import { extractPageContent, type ExtractedPageContent } from "./extractPageContent";
import { createStyles } from "./styles";

type WidgetConfig = {
  siteId: string;
  displayName: string;
  welcomeMessage: string;
  primaryColor: string;
  summaryActionText: string;
  enabled: boolean;
};

type QuickQuestionsResponse = {
  questions?: string[];
  error?: string;
};

const DEFAULT_CONFIG: WidgetConfig = {
  siteId: "",
  displayName: "Website Assistant",
  welcomeMessage: "Ask me anything about this page.",
  primaryColor: "#2563eb",
  summaryActionText: "Summarize this page",
  enabled: true,
};

const URL_PATTERN = /https?:\/\/[^\s)]+/g;
const CONTEXT_DEBOUNCE_MS = 600;
const ACCESSIBLE_MODE_CLASS = "aa-accessible-mode-active";
const ACCESSIBLE_MODE_STYLE_ID = "aa-accessible-mode-global-style";
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

type HiddenSiblingState = {
  element: HTMLElement;
  ariaHidden: string | null;
  inert: boolean;
};

type FocusTarget = {
  focus: () => void;
};

function ensureAccessibleModeStyles() {
  if (document.getElementById(ACCESSIBLE_MODE_STYLE_ID)) {
    return;
  }

  const style = document.createElement("style");
  style.id = ACCESSIBLE_MODE_STYLE_ID;
  style.textContent = `
    html.${ACCESSIBLE_MODE_CLASS},
    html.${ACCESSIBLE_MODE_CLASS} body {
      overflow: hidden !important;
    }

    html.${ACCESSIBLE_MODE_CLASS} body > :not(allow-access-widget) {
      opacity: 0 !important;
      pointer-events: none !important;
      user-select: none !important;
    }

    html.${ACCESSIBLE_MODE_CLASS} allow-access-widget {
      display: block !important;
      inset: 0 !important;
      position: fixed !important;
      visibility: visible !important;
      z-index: 2147483647 !important;
    }
  `;
  document.head.append(style);
}

function getScriptSettings() {
  const script = document.currentScript as HTMLScriptElement | null;
  const fallbackScript = document.querySelector<HTMLScriptElement>("script[data-site-id]");
  const sourceScript = script ?? fallbackScript;
  const baseUrl = sourceScript?.src ? new URL(sourceScript.src).origin : window.location.origin;

  return {
    siteId: sourceScript?.dataset.siteId ?? "",
    baseUrl,
  };
}

function simplifyMarkdown(value: string) {
  return value
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .trim();
}

function getReadableUrl(url: string) {
  try {
    const parsedUrl = new URL(url);
    return `${parsedUrl.hostname}${parsedUrl.pathname === "/" ? "" : parsedUrl.pathname}`;
  } catch {
    return url;
  }
}

function splitTrailingPunctuation(url: string) {
  const match = url.match(/^(.+?)([.,;:!?]+)?$/);
  return {
    cleanUrl: match?.[1] ?? url,
    trailingPunctuation: match?.[2] ?? "",
  };
}

function isExternalUrl(url: string) {
  try {
    return new URL(url, window.location.href).origin !== window.location.origin;
  } catch {
    return true;
  }
}

function appendTextWithLinks(parent: HTMLElement, text: string) {
  let lastIndex = 0;

  for (const match of text.matchAll(URL_PATTERN)) {
    const { cleanUrl, trailingPunctuation } = splitTrailingPunctuation(match[0]);
    const index = match.index ?? 0;

    if (index > lastIndex) {
      parent.append(document.createTextNode(text.slice(lastIndex, index)));
    }

    const link = document.createElement("a");
    link.href = cleanUrl;

    if (isExternalUrl(cleanUrl)) {
      link.target = "_blank";
      link.rel = "noopener noreferrer";
    }

    link.textContent = getReadableUrl(cleanUrl);
    link.setAttribute("aria-label", `Open ${cleanUrl}`);
    parent.append(link);

    if (trailingPunctuation) {
      parent.append(document.createTextNode(trailingPunctuation));
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < text.length) {
    parent.append(document.createTextNode(text.slice(lastIndex)));
  }
}

function appendParagraph(parent: HTMLElement, lines: string[]) {
  const paragraphText = simplifyMarkdown(lines.join(" "));

  if (!paragraphText) {
    return;
  }

  const paragraph = document.createElement("p");
  appendTextWithLinks(paragraph, paragraphText);
  parent.append(paragraph);
}

function renderAssistantContent(message: HTMLElement, text: string) {
  const lines = text.split(/\r?\n/);
  let paragraphLines: string[] = [];
  let list: HTMLUListElement | null = null;

  function flushParagraph() {
    appendParagraph(message, paragraphLines);
    paragraphLines = [];
  }

  for (const line of lines) {
    const trimmedLine = line.trim();
    const listItemMatch = trimmedLine.match(/^[-*]\s+(.*)$/);

    if (!trimmedLine) {
      flushParagraph();
      list = null;
      continue;
    }

    if (listItemMatch) {
      flushParagraph();

      if (!list) {
        list = document.createElement("ul");
        message.append(list);
      }

      const item = document.createElement("li");
      appendTextWithLinks(item, simplifyMarkdown(listItemMatch[1]));
      list.append(item);
      continue;
    }

    list = null;
    paragraphLines.push(trimmedLine);
  }

  flushParagraph();

  if (!message.hasChildNodes()) {
    message.textContent = text;
  }
}

function appendMessage(messages: HTMLElement, role: "assistant" | "user", text: string) {
  const message = document.createElement("div");
  message.className = "aa-message";
  message.dataset.role = role;
  message.setAttribute("role", "group");
  message.setAttribute("aria-label", role === "assistant" ? "Assistant message" : "Your message");

  if (role === "assistant") {
    renderAssistantContent(message, text);
  } else {
    message.textContent = text;
  }

  messages.append(message);
  messages.scrollTop = messages.scrollHeight;
}

async function loadConfig(baseUrl: string, siteId: string): Promise<WidgetConfig> {
  const response = await fetch(`${baseUrl}/api/widget/config?siteId=${encodeURIComponent(siteId)}`);
  const data = (await response.json().catch(() => null)) as { config?: WidgetConfig; error?: string } | null;

  if (!response.ok) {
    throw new Error(data?.error ?? "Widget configuration could not be loaded.");
  }

  return { ...DEFAULT_CONFIG, ...data?.config };
}

async function sendQuestion(
  baseUrl: string,
  siteId: string,
  question: string,
  pageContext: ExtractedPageContent,
) {
  const response = await fetch(`${baseUrl}/api/chat`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...pageContext,
      siteId,
      question,
    }),
  });

  const data = (await response.json()) as {
    answer?: string;
    sources?: Array<{ url: string; title?: string | null }>;
    error?: string;
  };

  if (!response.ok) {
    throw new Error(data.error ?? "The assistant could not answer right now.");
  }

  return {
    answer: data.answer ?? "I could not find an answer on this page.",
    sources: data.sources ?? [],
  };
}

async function loadQuickQuestions(
  baseUrl: string,
  siteId: string,
  pageContext: ExtractedPageContent,
) {
  const response = await fetch(`${baseUrl}/api/widget/quick-questions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...pageContext,
      siteId,
    }),
  });

  const data = (await response.json().catch(() => null)) as QuickQuestionsResponse | null;

  if (!response.ok) {
    throw new Error(data?.error ?? "Quick questions could not be generated.");
  }

  return data?.questions ?? [];
}

type PageContextManager = {
  get: () => Promise<ExtractedPageContent>;
  scheduleRefresh: () => void;
  dispose: () => void;
};

function createPageContextManager(onUpdate?: (context: ExtractedPageContent) => void): PageContextManager {
  let cached: ExtractedPageContent | null = null;
  let inflight: Promise<ExtractedPageContent> | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let lastUrl = window.location.href;
  let mutationObserver: MutationObserver | null = null;
  let disposed = false;

  async function refresh() {
    if (disposed) {
      return cached ?? extractPageContent();
    }
    inflight = extractPageContent();
    try {
      const next = await inflight;
      cached = next;
      onUpdate?.(next);
      return next;
    } finally {
      inflight = null;
    }
  }

  function scheduleRefresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refresh();
    }, CONTEXT_DEBOUNCE_MS);
  }

  function handlePotentialNavigation() {
    const currentUrl = window.location.href;
    if (currentUrl !== lastUrl) {
      lastUrl = currentUrl;
      cached = null;
      scheduleRefresh();
    }
  }

  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function patchedPushState(...args: Parameters<History["pushState"]>) {
    const result = originalPushState(...args);
    handlePotentialNavigation();
    return result;
  };

  history.replaceState = function patchedReplaceState(...args: Parameters<History["replaceState"]>) {
    const result = originalReplaceState(...args);
    handlePotentialNavigation();
    return result;
  };

  window.addEventListener("popstate", handlePotentialNavigation);
  window.addEventListener("hashchange", handlePotentialNavigation);

  if (typeof MutationObserver !== "undefined") {
    mutationObserver = new MutationObserver((mutations) => {
      const meaningful = mutations.some((mutation) => {
        if (mutation.type === "childList") {
          return Array.from(mutation.addedNodes).some(
            (node) => node.nodeType === Node.ELEMENT_NODE || (node.textContent?.length ?? 0) > 40,
          );
        }
        if (mutation.type === "characterData") {
          return (mutation.target.textContent?.length ?? 0) > 40;
        }
        return false;
      });
      if (meaningful) {
        scheduleRefresh();
      }
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  void refresh();

  return {
    async get() {
      if (cached) {
        if (cached.pageUrl !== window.location.href) {
          return refresh();
        }
        return cached;
      }
      if (inflight) {
        return inflight;
      }
      return refresh();
    },
    scheduleRefresh,
    dispose() {
      disposed = true;
      if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
      }
      mutationObserver?.disconnect();
      window.removeEventListener("popstate", handlePotentialNavigation);
      window.removeEventListener("hashchange", handlePotentialNavigation);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    },
  };
}

function appendSourceList(messages: HTMLElement, sources: Array<{ url: string; title?: string | null }>) {
  if (!sources.length) {
    return;
  }
  const wrapper = document.createElement("div");
  wrapper.className = "aa-sources";
  wrapper.setAttribute("aria-label", "Sources");

  const label = document.createElement("p");
  label.className = "aa-sources-label";
  label.textContent = "Sources";
  wrapper.append(label);

  const list = document.createElement("ul");
  for (const source of sources.slice(0, 5)) {
    const item = document.createElement("li");
    const link = document.createElement("a");
    link.href = source.url;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = source.title?.trim() || getReadableUrl(source.url);
    link.setAttribute("aria-label", `Open ${source.url}`);
    item.append(link);
    list.append(item);
  }
  wrapper.append(list);
  messages.append(wrapper);
  messages.scrollTop = messages.scrollHeight;
}

function createWidget(config: WidgetConfig, baseUrl: string, initialError = "") {
  ensureAccessibleModeStyles();

  const host = document.createElement("allow-access-widget");
  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  style.textContent = createStyles(config.primaryColor);

  const accessibleModeBackdrop = document.createElement("div");
  accessibleModeBackdrop.className = "aa-accessible-mode-backdrop";
  accessibleModeBackdrop.setAttribute("aria-hidden", "true");

  const button = document.createElement("button");
  button.className = "aa-button";
  button.type = "button";
  button.setAttribute("aria-label", "Open website assistant");
  button.setAttribute("aria-expanded", "false");
  button.setAttribute("aria-controls", "aa-widget-panel");
  button.innerHTML = `<span class="aa-button-mark" aria-hidden="true">AI</span><span>Ask AI</span>`;

  const panel = document.createElement("section");
  panel.className = "aa-panel";
  panel.dataset.open = "false";
  panel.id = "aa-widget-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "false");
  panel.setAttribute("aria-labelledby", "aa-widget-title");
  panel.setAttribute("aria-describedby", "aa-widget-description");
  panel.tabIndex = -1;
  panel.innerHTML = `
    <header class="aa-header">
      <div>
        <p class="aa-eyebrow">Page assistant</p>
        <h2 class="aa-title" id="aa-widget-title"></h2>
        <p class="aa-sr-only" id="aa-widget-description">
          Ask questions about this page, choose suggested quick questions, or enter accessible chat mode.
        </p>
      </div>
      <button class="aa-close" type="button" aria-label="Close assistant">Close</button>
    </header>
    <div class="aa-mode-row">
      <button
        class="aa-accessible-mode-button"
        type="button"
        aria-controls="aa-widget-panel"
        aria-expanded="false"
        aria-pressed="false"
      >
        Accessible Chat Mode
      </button>
    </div>
    <div class="aa-messages" role="log" aria-live="polite" aria-relevant="additions"></div>
    <div class="aa-actions"></div>
    <form class="aa-form">
      <label class="aa-label" for="aa-widget-question">Ask a question about this page</label>
      <div class="aa-input-row">
        <input class="aa-input" id="aa-widget-question" name="question" autocomplete="off" placeholder="Ask about this page..." />
        <button class="aa-send" type="submit">Send</button>
      </div>
    </form>
    <div class="aa-status" role="status" aria-live="polite"></div>
    <div class="aa-error" role="alert"></div>
  `;

  shadow.append(style, accessibleModeBackdrop, panel, button);
  document.body.append(host);

  const title = panel.querySelector<HTMLElement>(".aa-title");
  const close = panel.querySelector<HTMLButtonElement>(".aa-close");
  const messages = panel.querySelector<HTMLElement>(".aa-messages");
  const actions = panel.querySelector<HTMLElement>(".aa-actions");
  const form = panel.querySelector<HTMLFormElement>(".aa-form");
  const input = panel.querySelector<HTMLInputElement>(".aa-input");
  const send = panel.querySelector<HTMLButtonElement>(".aa-send");
  const status = panel.querySelector<HTMLElement>(".aa-status");
  const error = panel.querySelector<HTMLElement>(".aa-error");
  const accessibleModeButton = panel.querySelector<HTMLButtonElement>(".aa-accessible-mode-button");

  if (!title || !close || !messages || !actions || !form || !input || !send || !status || !error || !accessibleModeButton) {
    return;
  }

  const messagesElement = messages;
  const closeElement = close;
  const inputElement = input;
  const sendElement = send;
  const statusElement = status;
  const errorElement = error;
  const accessibleModeButtonElement = accessibleModeButton;
  let accessibleModeActive = false;
  let hiddenSiblingStates: HiddenSiblingState[] = [];
  let focusBeforeOpen: FocusTarget | null = null;

  title.textContent = config.displayName;
  appendMessage(messagesElement, "assistant", config.welcomeMessage);
  errorElement.textContent = initialError;

  const summaryButton = document.createElement("button");
  summaryButton.className = "aa-chip";
  summaryButton.type = "button";
  summaryButton.textContent = config.summaryActionText;
  actions.append(summaryButton);

  const quickQuestionCache = new Map<string, string[]>();
  let quickQuestionButtons: HTMLButtonElement[] = [];
  let quickQuestionRequestId = 0;

  const quickQuestionsWrapper = document.createElement("div");
  quickQuestionsWrapper.className = "aa-quick-questions";
  quickQuestionsWrapper.setAttribute("role", "group");
  quickQuestionsWrapper.setAttribute("aria-label", "Suggested quick questions");
  quickQuestionsWrapper.setAttribute("aria-live", "polite");
  actions.append(quickQuestionsWrapper);

  function setQuickQuestionButtonsDisabled(isDisabled: boolean) {
    for (const quickQuestionButton of quickQuestionButtons) {
      quickQuestionButton.disabled = isDisabled;
    }
  }

  function renderQuickQuestions(questions: string[], isLoading = false) {
    quickQuestionsWrapper.replaceChildren();
    quickQuestionButtons = [];

    if (isLoading) {
      const status = document.createElement("p");
      status.className = "aa-quick-questions-status";
      status.textContent = "Generating quick questions...";
      quickQuestionsWrapper.append(status);
      return;
    }

    if (!questions.length) {
      return;
    }

    const label = document.createElement("p");
    label.className = "aa-quick-questions-label";
    label.textContent = "Quick questions";
    quickQuestionsWrapper.append(label);

    const list = document.createElement("div");
    list.className = "aa-quick-questions-list";
    for (const question of questions) {
      const quickQuestionButton = document.createElement("button");
      quickQuestionButton.className = "aa-chip aa-quick-question-chip";
      quickQuestionButton.type = "button";
      quickQuestionButton.textContent = question;
      quickQuestionButton.addEventListener("click", () => {
        void ask(question);
      });
      quickQuestionButtons.push(quickQuestionButton);
      list.append(quickQuestionButton);
    }
    quickQuestionsWrapper.append(list);
  }

  async function refreshQuickQuestions(context: ExtractedPageContent) {
    const cachedQuestions = quickQuestionCache.get(context.contentHash);
    const requestId = ++quickQuestionRequestId;

    if (cachedQuestions) {
      renderQuickQuestions(cachedQuestions);
      return;
    }

    renderQuickQuestions([], true);

    try {
      const questions = await loadQuickQuestions(baseUrl, config.siteId, context);
      quickQuestionCache.set(context.contentHash, questions);
      if (requestId === quickQuestionRequestId) {
        renderQuickQuestions(questions);
      }
    } catch {
      if (requestId === quickQuestionRequestId) {
        renderQuickQuestions([]);
      }
    }
  }

  if (initialError) {
    inputElement.disabled = true;
    sendElement.disabled = true;
    summaryButton.disabled = true;
    quickQuestionsWrapper.hidden = true;
  }

  const contextManager = createPageContextManager((context) => {
    void refreshQuickQuestions(context);
  });

  function rememberFocusBeforeOpen() {
    const activeElement = shadow.activeElement ?? document.activeElement;
    if (activeElement && "focus" in activeElement && typeof activeElement.focus === "function") {
      focusBeforeOpen = activeElement as FocusTarget;
    } else {
      focusBeforeOpen = button;
    }
  }

  function restoreFocusAfterClose() {
    window.setTimeout(() => {
      const target =
        focusBeforeOpen instanceof HTMLElement && panel.contains(focusBeforeOpen)
          ? button
          : focusBeforeOpen ?? button;
      target.focus();
      focusBeforeOpen = null;
    }, 0);
  }

  function setPageSiblingsHidden(isHidden: boolean) {
    if (isHidden) {
      hiddenSiblingStates = Array.from(document.body.children)
        .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== host)
        .map((element) => ({
          element,
          ariaHidden: element.getAttribute("aria-hidden"),
          inert: element.inert,
        }));

      for (const { element } of hiddenSiblingStates) {
        element.setAttribute("aria-hidden", "true");
        element.inert = true;
      }
      return;
    }

    for (const { element, ariaHidden, inert } of hiddenSiblingStates) {
      if (ariaHidden === null) {
        element.removeAttribute("aria-hidden");
      } else {
        element.setAttribute("aria-hidden", ariaHidden);
      }
      element.inert = inert;
    }
    hiddenSiblingStates = [];
  }

  function focusInput() {
    window.setTimeout(() => {
      inputElement.focus();
    }, 0);
  }

  function trapPanelFocus(event: KeyboardEvent) {
    if (panel.dataset.open !== "true" || event.key !== "Tab") {
      return;
    }

    const focusableElements = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (element) => element.offsetParent !== null,
    );

    if (focusableElements.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    const activeElement = shadow.activeElement;

    if (event.shiftKey && activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function setOpen(isOpen: boolean) {
    const wasOpen = panel.dataset.open === "true";
    panel.dataset.open = String(isOpen);
    button.setAttribute("aria-expanded", String(isOpen));
    button.setAttribute("aria-label", isOpen ? "Close website assistant" : "Open website assistant");
    panel.setAttribute("aria-modal", String(isOpen || accessibleModeActive));

    if (isOpen) {
      if (!wasOpen) {
        rememberFocusBeforeOpen();
      }
      if (!accessibleModeActive) {
        contextManager.scheduleRefresh();
      }
      focusInput();
    } else if (wasOpen) {
      restoreFocusAfterClose();
    }
  }

  function setAccessibleMode(isActive: boolean) {
    accessibleModeActive = isActive;
    host.dataset.accessibleMode = String(isActive);
    accessibleModeButtonElement.setAttribute("aria-pressed", String(isActive));
    accessibleModeButtonElement.setAttribute("aria-expanded", String(isActive));
    accessibleModeButtonElement.textContent = isActive ? "Exit Accessible Chat Mode" : "Accessible Chat Mode";
    panel.setAttribute("aria-modal", String(isActive || panel.dataset.open === "true"));
    closeElement.textContent = isActive ? "Exit accessible mode" : "Close";
    closeElement.setAttribute("aria-label", isActive ? "Exit accessible chat mode" : "Close assistant");
    document.documentElement.classList.toggle(ACCESSIBLE_MODE_CLASS, isActive);
    setPageSiblingsHidden(isActive);
  }

  async function enterAccessibleMode() {
    errorElement.textContent = "";
    statusElement.textContent = "Preparing accessible chat mode.";
    accessibleModeButtonElement.disabled = true;

    try {
      await contextManager.get();
      setAccessibleMode(true);
      setOpen(true);
    } catch (caught) {
      errorElement.textContent = caught instanceof Error ? caught.message : "Accessible chat mode could not start.";
      setAccessibleMode(true);
      setOpen(true);
    } finally {
      statusElement.textContent = "";
      accessibleModeButtonElement.disabled = false;
      focusInput();
    }
  }

  function exitAccessibleMode() {
    setAccessibleMode(false);
    setOpen(false);
  }

  button.addEventListener("click", () => {
    setOpen(panel.dataset.open !== "true");
  });

  accessibleModeButtonElement.addEventListener("click", () => {
    if (accessibleModeActive) {
      exitAccessibleMode();
      return;
    }

    void enterAccessibleMode();
  });

  closeElement.addEventListener("click", () => {
    if (accessibleModeActive) {
      exitAccessibleMode();
      return;
    }

    setOpen(false);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      trapPanelFocus(event);
      return;
    }

    if (accessibleModeActive) {
      exitAccessibleMode();
      return;
    }

    if (panel.dataset.open === "true") {
      setOpen(false);
    }
  });

  async function ask(question: string) {
    errorElement.textContent = "";
    statusElement.textContent = "Reading this page and preparing an answer.";
    sendElement.disabled = true;
    summaryButton.disabled = true;
    setQuickQuestionButtonsDisabled(true);
    appendMessage(messagesElement, "user", question);

    try {
      const pageContext = await contextManager.get();
      statusElement.textContent = "Asking the assistant.";
      const { answer, sources } = await sendQuestion(baseUrl, config.siteId, question, pageContext);
      appendMessage(messagesElement, "assistant", answer);
      appendSourceList(messagesElement, sources);
    } catch (caught) {
      errorElement.textContent = caught instanceof Error ? caught.message : "Something went wrong.";
    } finally {
      statusElement.textContent = "";
      sendElement.disabled = false;
      summaryButton.disabled = false;
      setQuickQuestionButtonsDisabled(false);
    }
  }

  summaryButton.addEventListener("click", () => {
    void ask("Summarize this page and list the most useful links.");
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const question = inputElement.value.trim();

    if (!question) {
      return;
    }

    inputElement.value = "";
    void ask(question);
  });
}

async function boot() {
  const { siteId, baseUrl } = getScriptSettings();

  if (!siteId) {
    console.warn("AllowAccess widget missing data-site-id.");
    return;
  }

  try {
    const config = await loadConfig(baseUrl, siteId);

    if (config.enabled) {
      createWidget(config, baseUrl);
    }
  } catch (error) {
    createWidget(
      { ...DEFAULT_CONFIG, siteId },
      baseUrl,
      error instanceof Error ? error.message : "Widget configuration could not be loaded.",
    );
    console.warn(error);
  }
}

void boot();
